// Brand subscription plan vocabulary (mirrors desirelist-api lib/brandPlans).
//
//   plan       free | standard_brand | enterprise_brand | enterprise_rmn
//              | enterprise_agency
//   network    enterprise_rmn | enterprise_agency — the brand is a network
//              root: it sees Catalog > Brands and holds child brands. The API
//              derives brands.rmn from this, so `rmn === true` and
//              isNetworkPlan(plan) always agree on a migrated row.
//
// Labels are internal-facing (admin chips, forms). Client-visible copy on
// the public /pricing page comes from the API's plan catalog.

export type BrandPlan =
  | 'free'
  | 'standard_brand'
  | 'enterprise_brand'
  | 'enterprise_rmn'
  | 'enterprise_agency';

export type PdpScoreInterval = 'daily' | 'weekly' | 'biweekly' | 'monthly';

export const BRAND_PLANS: BrandPlan[] = [
  'free',
  'standard_brand',
  'enterprise_brand',
  'enterprise_rmn',
  'enterprise_agency',
];

export const PLAN_LABELS: Record<BrandPlan, string> = {
  free: 'Free',
  standard_brand: 'Standard Brand',
  enterprise_brand: 'Enterprise Brand',
  enterprise_rmn: 'Enterprise RMN',
  enterprise_agency: 'Enterprise Agency',
};

export const NETWORK_PLANS: readonly BrandPlan[] = ['enterprise_rmn', 'enterprise_agency'];

export const PDP_SCORE_INTERVALS: PdpScoreInterval[] = ['daily', 'weekly', 'biweekly', 'monthly'];

export const INTERVAL_LABELS: Record<PdpScoreInterval, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
};

export const isBrandPlan = (v: unknown): v is BrandPlan =>
  typeof v === 'string' && (BRAND_PLANS as string[]).includes(v);

export const isNetworkPlan = (plan?: string | null): boolean =>
  !!plan && (NETWORK_PLANS as string[]).includes(plan);

// Derived-or-explicit: the API's derived rmn flag OR the plan says network.
export const brandIsNetwork = (b?: { rmn?: boolean; plan?: string | null } | null): boolean =>
  !!b && (b.rmn === true || isNetworkPlan(b.plan));

export const planLabel = (plan?: string | null): string =>
  isBrandPlan(plan) ? PLAN_LABELS[plan] : plan || '—';

// The day a weekly / monthly analysis runs on (lib/analysisSchedule): the
// weekday index as JavaScript counts them (0 = Sunday), or the date.
export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);
export const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// "Weekly on Tuesdays", "Monthly on the 15th", "Daily".
export const scheduleLabel = (interval?: string | null, weekday?: number | null, dayOfMonth?: number | null): string => {
  const base = intervalLabel(interval);
  if ((interval === 'weekly' || interval === 'biweekly') && weekday !== null && weekday !== undefined && WEEKDAY_LABELS[weekday]) {
    return `${base} on ${WEEKDAY_LABELS[weekday]}s`;
  }
  if (interval === 'monthly' && dayOfMonth) return `${base} on the ${ordinal(dayOfMonth)}`;
  return base;
};

export const intervalLabel = (v?: string | null): string =>
  v && (PDP_SCORE_INTERVALS as string[]).includes(v)
    ? INTERVAL_LABELS[v as PdpScoreInterval]
    : v || '—';

export const formatProductCap = (max: number | null | undefined): string =>
  max === null || max === undefined ? 'Unlimited' : String(max);

// The API answers activating PDP scoring (creating an active PDP URL, or
// PATCH active:true) beyond the account brand's max_products with
// 409 { error: 'ScoringLimitReached', limit, count, brand_id }. Importing
// products is never limited. Returns a client-ready message, or null.
export const scoringLimitMessage = (err: any): string | null => {
  const data = err && err.response ? err.response.data : null;
  const code = typeof data === 'string' ? data : data && data.error;
  if (code === 'ScoringInactive') {
    return 'Scoring is paused for this PDP URL. Activate scoring first.';
  }
  if (code !== 'ScoringLimitReached') return null;
  const limit = data && typeof data === 'object' ? data.limit : null;
  const count = data && typeof data === 'object' ? data.count : null;
  const detail =
    limit !== null && limit !== undefined && count !== null && count !== undefined
      ? ` (${count} of ${limit} products scoring)`
      : '';
  return `Your plan's scoring limit has been reached${detail}. Pause scoring on another product or upgrade your plan.`;
};
